import { useState } from 'preact/hooks'
import { addDays } from '~/calculations/calendar'
import type { ProjectSummary, SummaryChange } from '~/calculations/projectSummary'
import { Button } from '~/components/primitives/Button'
import { DateField, TextField } from '~/components/primitives/Field'
import { DESIRED_OUTCOME_LABELS, type ProjectRecord } from '~/models/project'
import {
  MAX_PROJECT_START_DAYS_AHEAD,
  type ProjectEndState,
  type ProjectStartState,
} from '~/services/projectService'
import { toCalendarDate, unwrapDate } from '~/types/dates'
import { formatCalendarDate } from '~/utils/format'
import styles from './ProjectSection.module.css'

/**
 * The project, its end decision, and the summary of a finished one (D-062,
 * D-020).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THREE CHOICES, ALL EXPLICIT, AND NONE OF THEM STARTS ANYTHING NEW.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Extend, complete, or abandon — and leaving it running is a real fourth option
 * presented as such rather than as the absence of a decision. This is the phase
 * decision one level up, and the same rule governs it: reaching the target date
 * warns and changes nothing.
 *
 * **A completion may suggest a next project. It never creates one.** The
 * suggestion below is a sentence and a link back to setup, not a button that
 * quietly starts a second undertaking on the strength of finishing the first.
 *
 * **The summary reports counts and coverage.** No score, no grade, no
 * percentage, no streak — and no verdict against the success criteria the route
 * engine stated at setup, because a met/not-met result is a grade in all but
 * name.
 */

type SaveHandler<T> = (input: T) => Promise<string | undefined>

interface ProjectSectionProps {
  endState: ProjectEndState
  /**
   * Whether the project has begun (D-107 §2).
   *
   * Passed in rather than derived from `endState.project.startDate` here, because
   * the record names one accessor for this and a second local comparison is how
   * two definitions of "started" begin.
   */
  startState: ProjectStartState
  projects: readonly ProjectRecord[]
  onExtend: SaveHandler<string>
  onEnd: SaveHandler<{ status: 'completed' | 'abandoned'; endedOn: string; reason?: string }>
  onExportSummary: (project: ProjectRecord) => Promise<void>
  /** Move the start date of a project that has not begun (D-107 §5). */
  onReschedule: SaveHandler<string>
  /** Discard a project that has not begun (D-107 §6). */
  onDiscard: () => Promise<string | undefined>
  today: string
}

export function ProjectSection(props: ProjectSectionProps) {
  return (
    <>
      <ActiveProject {...props} />
      <ProjectHistory projects={props.projects} onExport={props.onExportSummary} />
    </>
  )
}

/**
 * The latest start date a reschedule will accept.
 *
 * The seven-day cap, additionally clamped by the target date — a project cannot
 * begin after the day it is aiming for.
 */
function latestStart(today: string, targetDate: string): string {
  const parsed = toCalendarDate(today)
  const cap =
    parsed === undefined ? today : unwrapDate(addDays(parsed, MAX_PROJECT_START_DAYS_AHEAD))
  return cap < targetDate ? cap : targetDate
}

function ActiveProject({
  endState,
  startState,
  onExtend,
  onEnd,
  onReschedule,
  onDiscard,
  today,
}: ProjectSectionProps) {
  /* A project that has not begun (D-107). Read from the one accessor. */
  const waiting = !startState.hasStarted
  const [choice, setChoice] = useState<
    'none' | 'extend' | 'complete' | 'abandon' | 'reschedule' | 'discard'
  >('none')
  const [startDate, setStartDate] = useState('')
  const [targetDate, setTargetDate] = useState('')
  const [endedOn, setEndedOn] = useState(today)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)

  const project = endState.project
  if (project === undefined) {
    return (
      <section aria-labelledby="project-heading" class={styles.section}>
        <h2 id="project-heading" class={styles.heading}>
          Project
        </h2>
        <p class={styles.empty}>
          No project is running. Project Sola will not start one on its own — setting one up is
          something you do.
        </p>
      </section>
    )
  }

  async function run(action: () => Promise<string | undefined>) {
    setBusy(true)
    const message = await action()
    setBusy(false)
    setError(message)
    if (message === undefined) setChoice('none')
  }

  return (
    <section aria-labelledby="project-heading" class={styles.section}>
      <h2 id="project-heading" class={styles.heading}>
        Project
      </h2>

      <dl class={styles.summary}>
        <div class={styles.row}>
          <dt>Name</dt>
          <dd>{project.name}</dd>
        </div>
        {project.desiredOutcome === undefined ? null : (
          <div class={styles.row}>
            <dt>Aiming for</dt>
            <dd>{DESIRED_OUTCOME_LABELS[project.desiredOutcome]}</dd>
          </div>
        )}
        <div class={styles.row}>
          {/* "Starts", not "Started", while the day is still ahead. One word, and
              it is the difference between a fact and a false statement (D-107). */}
          <dt>{waiting ? 'Starts' : 'Started'}</dt>
          <dd>{formatCalendarDate(project.startDate)}</dd>
        </div>
        <div class={styles.row}>
          <dt>Target date</dt>
          <dd>{formatCalendarDate(project.targetDate)}</dd>
        </div>
      </dl>

      {waiting ? (
        <p class={styles.notice}>
          <strong>This project has not started yet.</strong> Your first week, your sessions and your
          weekly review all begin on {formatCalendarDate(project.startDate)}. Until then you can
          change the date or discard it — anything you record in the meantime is kept.
        </p>
      ) : null}

      {/* Not while waiting: extending, completing and setting aside are all
          refused by the service before a project starts, so offering three
          actions of which none can run would be an invitation to a dead end. */}
      {!waiting && endState.reachedTargetDate ? (
        <p class={styles.notice}>
          <strong>This project has reached its target date.</strong> Nothing has changed and nothing
          will until you decide. You can extend it, complete it, or set it aside.
        </p>
      ) : null}

      {choice === 'none' ? (
        <div class={styles.actions}>
          {waiting ? (
            <>
              <Button variant="secondary" onClick={() => setChoice('reschedule')}>
                Change the start date
              </Button>
              <Button variant="secondary" onClick={() => setChoice('discard')}>
                Discard this project
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => setChoice('extend')}>
                Extend it
              </Button>
              <Button variant="secondary" onClick={() => setChoice('complete')}>
                Complete it
              </Button>
              <Button variant="secondary" onClick={() => setChoice('abandon')}>
                Set it aside
              </Button>
            </>
          )}
        </div>
      ) : null}

      {choice === 'reschedule' ? (
        <div class={styles.form}>
          <DateField
            label="New start date"
            hint="Today, or up to a week from now. Your first week, your sessions and your weekly review all move with it."
            min={today}
            max={latestStart(today, project.targetDate)}
            value={startDate}
            onInput={setStartDate}
          />
          <div class={styles.actions}>
            <Button onClick={() => void run(() => onReschedule(startDate))} busy={busy}>
              Move the start date
            </Button>
            <Button variant="secondary" onClick={() => setChoice('none')}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {/* Inline, never a sheet (D-104 §3, D-107 §6). A dismissible overlay makes
          a backdrop tap a way to lose a destructive confirmation mid-thought.
          Both lists are named because the point of the decision is that the
          second one is not empty. */}
      {choice === 'discard' ? (
        <div class={styles.form}>
          <p class={styles.notice}>
            <strong>Discard this project?</strong> This removes the project, its phase, the date you
            were aiming for, the areas you chose and your nutrition plan.
          </p>
          <p class={styles.notice}>
            <strong>Your measurements, your weigh-ins and your photos are kept.</strong> You
            recorded them, and a plan you never started has no claim on them. They stay in Progress
            and Photos.
          </p>
          <div class={styles.actions}>
            <Button onClick={() => void run(onDiscard)} busy={busy}>
              Discard it
            </Button>
            <Button variant="secondary" onClick={() => setChoice('none')}>
              Keep it
            </Button>
          </div>
        </div>
      ) : null}

      {choice === 'extend' ? (
        <div class={styles.form}>
          <DateField
            label="New target date"
            hint="Moving it changes nothing else. The project carries on as it was."
            min={project.startDate as string}
            value={targetDate}
            onInput={setTargetDate}
          />
          <div class={styles.actions}>
            <Button onClick={() => void run(() => onExtend(targetDate))} busy={busy}>
              Move the date
            </Button>
            <Button variant="secondary" onClick={() => setChoice('none')}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {choice === 'complete' || choice === 'abandon' ? (
        <div class={styles.form}>
          <p class={styles.hint}>
            {choice === 'complete'
              ? 'Completing writes a summary of what you recorded and keeps the project as history. It does not start anything new.'
              : 'Setting a project aside keeps everything you recorded. It is not a failure and nothing is deleted.'}
          </p>
          <DateField
            label={choice === 'complete' ? 'Date it finished' : 'Date you stopped'}
            min={project.startDate as string}
            max={today}
            value={endedOn}
            onInput={setEndedOn}
          />
          <TextField
            label="Why (optional)"
            hint="For your own record later. Kept out of exports unless you include notes."
            value={reason}
            onInput={setReason}
            multiline
          />
          <div class={styles.actions}>
            <Button
              onClick={() =>
                void run(() =>
                  onEnd({
                    status: choice === 'complete' ? 'completed' : 'abandoned',
                    endedOn,
                    ...(reason.trim() === '' ? {} : { reason: reason.trim() }),
                  }),
                )
              }
              busy={busy}
            >
              {choice === 'complete' ? 'Complete this project' : 'Set it aside'}
            </Button>
            <Button variant="secondary" onClick={() => setChoice('none')}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      {error === undefined ? null : (
        <p class={styles.error} role="alert">
          {error}
        </p>
      )}
    </section>
  )
}

function ProjectHistory({
  projects,
  onExport,
}: {
  projects: readonly ProjectRecord[]
  onExport: (project: ProjectRecord) => Promise<void>
}) {
  const finished = projects.filter((project) => project.status !== 'active')
  if (finished.length === 0) return null

  return (
    <section aria-labelledby="project-history-heading" class={styles.section}>
      <h2 id="project-history-heading" class={styles.heading}>
        Past projects
      </h2>
      <ul class={styles.list}>
        {finished.map((project) => (
          <li key={project.id} class={styles.item}>
            <p class={styles.itemTitle}>{project.name}</p>
            <p class={styles.itemMeta}>
              {project.status === 'completed' ? 'Completed' : 'Set aside'}
              {project.endedOn === undefined ? '' : ` · ${formatCalendarDate(project.endedOn)}`}
            </p>
            {project.endReason === undefined ? null : (
              <p class={styles.itemMeta}>{project.endReason}</p>
            )}
            {project.completionSummary === undefined ? null : (
              <>
                <CompletionSummaryView summary={project.completionSummary} />
                {/* A separate, user-initiated export (D-062). The weekly
                    document keeps its five-week default; a project spans
                    months and is never disclosed by default. */}
                <div class={styles.actions}>
                  <Button variant="secondary" onClick={() => void onExport(project)}>
                    Export this summary
                  </Button>
                </div>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * What was recorded, and nothing about how well.
 *
 * Every line here is a count or one of the three coverage words. Adding a
 * proportion, a tick, a colour or a comparison against the setup criteria would
 * turn a record into a report card, which is what D-062 forbids.
 */
function CompletionSummaryView({ summary }: { summary: ProjectSummary }) {
  return (
    <dl class={styles.summary}>
      <div class={styles.row}>
        <dt>Ran for</dt>
        <dd>
          {summary.durationWeeks} weeks · {summary.phaseCount}{' '}
          {summary.phaseCount === 1 ? 'phase' : 'phases'}
        </dd>
      </div>
      <div class={styles.row}>
        <dt>Recorded</dt>
        <dd>
          {summary.weighInsRecorded} weigh-ins · {summary.measurementsRecorded} measurements ·{' '}
          {summary.reviewsCompleted} reviews
        </dd>
      </div>
      <div class={styles.row}>
        <dt>Weight</dt>
        <dd>{describeChange(summary.weightChangeKg, 'kg')}</dd>
      </div>
      <div class={styles.row}>
        <dt>Waist</dt>
        <dd>{describeChange(summary.waistChangeCm, 'cm')}</dd>
      </div>
      <div class={styles.row}>
        <dt>Coverage</dt>
        <dd>
          weigh-ins {summary.coverage.weighIns} · reviews {summary.coverage.reviews}
        </dd>
      </div>
    </dl>
  )
}

/** A missing value is never a zero, and never a blank either. */
function describeChange(change: SummaryChange, unit: string): string {
  if (change.kind === 'noComparison') {
    return change.reason === 'onlyOne'
      ? 'one reading only, so there is nothing to compare'
      : 'nothing recorded'
  }
  const sign = change.delta > 0 ? '+' : ''
  return `${change.from} → ${change.to} ${unit} (${sign}${Number(change.delta.toFixed(2))} ${unit})`
}
